import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface LightboxProps {
  isOpen: boolean;
  imageUrl: string;
  images?: string[]; // Array of all image URLs for navigation
  currentIndex?: number; // Current image index in the array
  onClose: () => void;
  caption?: string;
}

const Overlay = styled.div<{ isOpen: boolean; $isMenuCollapsed: boolean; $isMobile: boolean }>`
  position: fixed;
  top: ${props => props.$isMobile ? '0' : '80px'}; /* Full screen on mobile, below header on desktop */
  left: ${props => {
    if (props.$isMobile) return '0';
    return props.$isMenuCollapsed ? '80px' : '280px';
  }}; /* Full screen on mobile, after sidebar on desktop */
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999; /* Very high z-index to ensure it's above everything */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.$isMobile ? '10px' : '20px'};
  touch-action: ${props => props.$isMobile ? 'none' : 'auto'}; /* Prevent page zoom on mobile */
`;

const ImageContainer = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: ${props => props.$isMobile ? 'hidden' : 'visible'};
  touch-action: ${props => props.$isMobile ? 'none' : 'auto'}; /* Prevent page zoom on mobile */
`;

const LightboxImage = styled.img<{ $isMobile: boolean; $scale?: number; $translateX?: number; $translateY?: number }>`
  max-width: 100%;
  max-height: ${props => props.$isMobile ? 'calc(100% - 80px)' : 'calc(100% - 60px)'}; /* Leave space for caption and buttons */
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  user-select: none; /* Prevent image selection on mobile */
  -webkit-user-select: none;
  -webkit-touch-callout: none; /* Prevent iOS callout */
  touch-action: ${props => props.$isMobile ? 'none' : 'auto'}; /* Prevent page zoom on mobile */
  transform: ${props => {
    if (props.$isMobile && props.$scale !== undefined) {
      const scale = props.$scale || 1;
      const translateX = props.$translateX || 0;
      const translateY = props.$translateY || 0;
      return `translate(${translateX}px, ${translateY}px) scale(${scale})`;
    }
    return 'none';
  }};
  transform-origin: center center;
  transition: ${props => props.$isMobile && props.$scale === 1 ? 'transform 0.2s ease-out' : 'none'};
  cursor: ${props => props.$isMobile && (props.$scale || 1) > 1 ? 'move' : 'default'};
`;

const CloseButton = styled.button<{ $isMobile: boolean }>`
  position: fixed; /* Use fixed instead of absolute on mobile to ensure it's always visible */
  top: 100px; /* Default for desktop and tablets */
  right: ${props => props.$isMobile ? '15px' : '20px'};
  width: ${props => props.$isMobile ? '48px' : '40px'};
  height: ${props => props.$isMobile ? '48px' : '40px'};
  border-radius: 50%;
  background-color: ${props => props.$isMobile ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.2)'};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001; /* Even higher z-index to ensure it's always on top */
  border: ${props => props.$isMobile ? '2px solid rgba(255, 255, 255, 0.9)' : '2px solid rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation; /* Better touch handling on mobile */
  box-shadow: ${props => props.$isMobile ? '0 2px 8px rgba(0, 0, 0, 0.3)' : 'none'};
  pointer-events: auto; /* Ensure buttons are clickable */
  
  &:hover {
    background-color: ${props => props.$isMobile ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
    border-color: rgba(255, 255, 255, 0.9);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
    background-color: ${props => props.$isMobile ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.4)'};
  }
  
  @media (max-width: 767px) {
    /* Mobile positioning - below header */
    top: 70px !important;
    min-width: 48px;
    min-height: 48px;
    background-color: rgba(0, 0, 0, 0.7);
    border: 2px solid rgba(255, 255, 255, 0.9);
  }
`;

const NavButton = styled.button<{ position: 'left' | 'right'; $isMobile: boolean }>`
  position: ${props => props.$isMobile ? 'fixed' : 'absolute'};
  ${props => props.position === 'left' ? `left: ${props.$isMobile ? '15px' : '20px'};` : `right: ${props.$isMobile ? '15px' : '20px'};`}
  top: 50%;
  transform: translateY(-50%);
  width: ${props => props.$isMobile ? '48px' : '50px'};
  height: ${props => props.$isMobile ? '48px' : '50px'};
  border-radius: 50%;
  background-color: ${props => props.$isMobile ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.2)'};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001; /* Very high z-index to ensure visibility */
  border: ${props => props.$isMobile ? '2px solid rgba(255, 255, 255, 0.9)' : '2px solid rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation; /* Better touch handling on mobile */
  box-shadow: ${props => props.$isMobile ? '0 2px 8px rgba(0, 0, 0, 0.3)' : 'none'};
  pointer-events: auto; /* Ensure buttons are clickable */
  
  &:hover {
    background-color: ${props => props.$isMobile ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
    border-color: rgba(255, 255, 255, 0.9);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
    background-color: ${props => props.$isMobile ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.4)'};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  @media (max-width: 767px) {
    /* Larger touch target on mobile with better visibility */
    min-width: 48px;
    min-height: 48px;
    background-color: rgba(0, 0, 0, 0.7);
    border: 2px solid rgba(255, 255, 255, 0.9);
  }
`;

const Caption = styled.p`
  color: #FFFFFF;
  margin-top: 10px;
  text-align: center;
  font-size: 14px;
`;

export const Lightbox: React.FC<LightboxProps> = ({ 
  isOpen, 
  imageUrl, 
  images,
  currentIndex: initialIndex,
  onClose,
  caption
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Pinch-to-zoom state for mobile
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const [lastTouchCenter, setLastTouchCenter] = useState<{ x: number; y: number } | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const imageRef = React.useRef<HTMLImageElement>(null);
  const lastTapRef = React.useRef<number>(0);
  
  // Detect mobile vs desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent page zoom when lightbox is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      // Prevent default zoom gestures on the document
      const preventZoom = (e: TouchEvent) => {
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };
      
      const preventDoubleTapZoom = (e: TouchEvent) => {
        // Allow double tap on image container, but prevent elsewhere
        const target = e.target as HTMLElement;
        if (!target.closest('[data-image-container]')) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', preventZoom, { passive: false });
      document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });
      
      // Prevent viewport zoom via meta tag manipulation
      const viewport = document.querySelector('meta[name="viewport"]');
      const originalContent = viewport?.getAttribute('content');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
      
      return () => {
        document.removeEventListener('touchmove', preventZoom);
        document.removeEventListener('touchend', preventDoubleTapZoom);
        if (viewport && originalContent) {
          viewport.setAttribute('content', originalContent);
        }
      };
    }
  }, [isOpen, isMobile]);
  
  // Detect menu state by checking header position (desktop only)
  useEffect(() => {
    if (!isOpen || isMobile) {
      setIsMenuCollapsed(true);
      return;
    }
    
    const checkMenuState = () => {
      // Check header's left position - it's positioned based on sidebar state
      const header = document.querySelector('header') as HTMLElement;
      if (header) {
        const left = parseInt(window.getComputedStyle(header).left, 10);
        setIsMenuCollapsed(left <= 100); // 80px collapsed, 280px expanded
      }
    };
    
    checkMenuState();
    // Check on resize and periodically
    window.addEventListener('resize', checkMenuState);
    const interval = setInterval(checkMenuState, 200);
    
    return () => {
      window.removeEventListener('resize', checkMenuState);
      clearInterval(interval);
    };
  }, [isOpen, isMobile]);
  
  // Determine if we have multiple images
  // If images array is provided and has items, use it; otherwise use imageUrl as fallback
  const imageArray = (images && images.length > 0) ? images : (imageUrl ? [imageUrl] : []);
  const hasMultipleImages = imageArray.length > 1;
  
  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('Lightbox opened:', { 
        imageUrl, 
        images, 
        imageArray, 
        currentIndex, 
        currentImage: imageArray[currentIndex] 
      });
    }
  }, [isOpen, imageUrl, images, imageArray, currentIndex]);
  
  // Set initial index
  useEffect(() => {
    if (isOpen) {
      if (initialIndex !== undefined && initialIndex >= 0 && initialIndex < imageArray.length) {
        setCurrentIndex(initialIndex);
      } else if (imageUrl && imageArray.length > 0) {
        // Find the index of the current imageUrl in the array
        const index = imageArray.findIndex(url => url === imageUrl);
        setCurrentIndex(index >= 0 ? index : 0);
      } else {
        setCurrentIndex(0);
      }
    }
  }, [initialIndex, imageArray.length, isOpen, imageUrl]);
  
  const handlePrevious = useCallback(() => {
    if (hasMultipleImages) {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageArray.length - 1));
    }
  }, [hasMultipleImages, imageArray.length]);
  
  const handleNext = useCallback(() => {
    if (hasMultipleImages) {
      setCurrentIndex((prev) => (prev < imageArray.length - 1 ? prev + 1 : 0));
    }
  }, [hasMultipleImages, imageArray.length]);
  
  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handlePrevious, handleNext, onClose]);

  // Reset zoom when image changes
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
      setLastTouchDistance(null);
      setLastTouchCenter(null);
      setIsZooming(false);
      setIsPanning(false);
    }
  }, [currentIndex, isOpen]);

  // Handle swipe gestures and pinch-to-zoom for mobile
  const minSwipeDistance = 50;
  const minZoom = 1;
  const maxZoom = 5;

  const getTouchDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touch1: Touch, touch2: Touch): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    
    // Don't prevent default if touching a button
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.tagName === 'BUTTON') {
      return; // Let button handle its own touch events
    }
    
    e.preventDefault(); // Prevent page zoom
    
    if (e.touches.length === 1) {
      // Single touch - prepare for swipe or pan
      setTouchEnd(null);
      setTouchStart(e.touches[0].clientX);
      
      // If zoomed, prepare for panning
      if (scale > 1) {
        setIsPanning(true);
        setLastTouchCenter({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      }
    } else if (e.touches.length === 2) {
      // Two touches - pinch to zoom
      setIsZooming(true);
      setIsPanning(false);
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const center = getTouchCenter(e.touches[0], e.touches[1]);
      setLastTouchDistance(distance);
      setLastTouchCenter(center);
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    
    // Don't prevent default if touching a button
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.tagName === 'BUTTON') {
      return; // Let button handle its own touch events
    }
    
    e.preventDefault(); // Prevent page zoom
    
    if (e.touches.length === 1 && scale > 1) {
      // Single touch while zoomed - panning
      if (isPanning && lastTouchCenter) {
        const deltaX = e.touches[0].clientX - lastTouchCenter.x;
        const deltaY = e.touches[0].clientY - lastTouchCenter.y;
        
        // Calculate bounds to prevent panning outside image
        if (imageRef.current) {
          const img = imageRef.current;
          const rect = img.getBoundingClientRect();
          const container = img.parentElement;
          if (container) {
            const containerRect = container.getBoundingClientRect();
            const maxTranslateX = (rect.width * scale - containerRect.width) / 2;
            const maxTranslateY = (rect.height * scale - containerRect.height) / 2;
            
            setTranslateX(prev => {
              const newX = prev + deltaX;
              return Math.max(-maxTranslateX, Math.min(maxTranslateX, newX));
            });
            setTranslateY(prev => {
              const newY = prev + deltaY;
              return Math.max(-maxTranslateY, Math.min(maxTranslateY, newY));
            });
          }
        }
        
        setLastTouchCenter({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      } else {
        setTouchEnd(e.touches[0].clientX);
      }
    } else if (e.touches.length === 2 && isZooming) {
      // Two touches - pinch to zoom
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const center = getTouchCenter(e.touches[0], e.touches[1]);
      
      if (lastTouchDistance !== null) {
        const scaleChange = distance / lastTouchDistance;
        const newScale = Math.max(minZoom, Math.min(maxZoom, scale * scaleChange));
        setScale(newScale);
        
        // Adjust translate to zoom towards touch center
        if (imageRef.current && lastTouchCenter) {
          const img = imageRef.current;
          const rect = img.getBoundingClientRect();
          const imgCenterX = rect.left + rect.width / 2;
          const imgCenterY = rect.top + rect.height / 2;
          
          const offsetX = center.x - imgCenterX;
          const offsetY = center.y - imgCenterY;
          
          setTranslateX(prev => prev + (offsetX * (scaleChange - 1)) / newScale);
          setTranslateY(prev => prev + (offsetY * (scaleChange - 1)) / newScale);
        }
      }
      
      setLastTouchDistance(distance);
      setLastTouchCenter(center);
    }
  };

  const onTouchEnd = (e: React.TouchEvent, skipDoubleTap = false) => {
    if (!isMobile) return;
    
    if (!skipDoubleTap) {
      e.preventDefault();
    }
    
    if (e.touches.length === 0) {
      // All touches ended
      setIsZooming(false);
      setIsPanning(false);
      setLastTouchDistance(null);
      setLastTouchCenter(null);
      
      // Handle swipe gesture if not zoomed
      if (scale === 1 && touchStart !== null && touchEnd !== null) {
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && hasMultipleImages) {
          handleNext();
        }
        if (isRightSwipe && hasMultipleImages) {
          handlePrevious();
        }
      }
      
      // Reset scale to 1 if zoomed out too far
      if (scale < minZoom) {
        setScale(minZoom);
        setTranslateX(0);
        setTranslateY(0);
      }
    } else if (e.touches.length === 1) {
      // One touch remaining - update for panning
      setLastTouchCenter({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };
  
  if (!isOpen || imageArray.length === 0) return null;

  const currentImage = imageArray[currentIndex] || imageArray[0];

  if (!currentImage) return null;

  return (
    <Overlay 
      isOpen={isOpen} 
      $isMenuCollapsed={isMenuCollapsed} 
      $isMobile={isMobile}
      onClick={onClose}
      onTouchStart={(e) => {
        // Don't prevent default if touching a button
        const target = e.target as HTMLElement;
        if (target.closest('button') || target.tagName === 'BUTTON') {
          return; // Let button handle its own touch events
        }
        // Only prevent default for image container touches
        if (target.closest('[data-image-container]')) {
          e.preventDefault();
        }
      }}
    >
      <CloseButton 
        $isMobile={isMobile}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClose();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClose();
        }}
        aria-label="Close lightbox"
      >
        <X size={isMobile ? 28 : 24} color="#FFFFFF" />
      </CloseButton>
      
      {hasMultipleImages && (
        <>
          <NavButton
            $isMobile={isMobile}
            position="left"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePrevious();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handlePrevious();
            }}
            aria-label="Previous image"
          >
            <ChevronLeft size={isMobile ? 28 : 24} color="#FFFFFF" />
          </NavButton>
          
          <NavButton
            $isMobile={isMobile}
            position="right"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleNext();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleNext();
            }}
            aria-label="Next image"
          >
            <ChevronRight size={isMobile ? 28 : 24} color="#FFFFFF" />
          </NavButton>
        </>
      )}
      
      <ImageContainer 
        $isMobile={isMobile}
        data-image-container
        onClick={(e) => {
          e.stopPropagation();
        }}
        onTouchStart={isMobile ? onTouchStart : undefined}
        onTouchMove={isMobile ? onTouchMove : undefined}
        onTouchEnd={(e) => {
          if (isMobile) {
            // Handle double tap to zoom
            if (e.touches.length === 0 && !isZooming && !isPanning && scale === 1) {
              const now = Date.now();
              const timeSinceLastTap = now - lastTapRef.current;
              
              if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
                // Double tap detected - zoom in
                e.preventDefault();
                setScale(2);
                lastTapRef.current = 0;
                return;
              } else {
                lastTapRef.current = now;
              }
            } else if (e.touches.length === 0 && !isZooming && !isPanning && scale > 1) {
              // Single tap when zoomed - zoom out
              const now = Date.now();
              const timeSinceLastTap = now - lastTapRef.current;
              
              if (timeSinceLastTap > 300) {
                // Not a double tap, zoom out
                e.preventDefault();
                setScale(1);
                setTranslateX(0);
                setTranslateY(0);
                lastTapRef.current = 0;
                return;
              }
            }
            
            onTouchEnd(e, true);
          }
        }}
      >
        <LightboxImage 
          ref={imageRef}
          $isMobile={isMobile}
          $scale={scale}
          $translateX={translateX}
          $translateY={translateY}
          key={`lightbox-image-${currentIndex}-${currentImage}`}
          src={currentImage} 
          alt={caption || `Lightbox image ${currentIndex + 1}`}
          onError={(e) => {
            console.error('Failed to load image:', currentImage);
            e.currentTarget.style.display = 'none';
          }}
          onLoad={(e) => {
            e.currentTarget.style.display = 'block';
            // Reset zoom when new image loads
            setScale(1);
            setTranslateX(0);
            setTranslateY(0);
          }}
        />
        {caption && (
          <Caption>{caption} {hasMultipleImages && `(${currentIndex + 1} / ${imageArray.length})`}</Caption>
        )}
        {!caption && hasMultipleImages && (
          <Caption>{currentIndex + 1} / {imageArray.length}</Caption>
        )}
      </ImageContainer>
    </Overlay>
  );
};
